import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Base } from './../Commons/Base';
import {
    GetOperators,
    RegisterOperator,
    UpdateOperator,
    DeleteOperator,
    TerminateOperatorSessions,
    ChangePassword,
    GetRoleBadgeConfig,
    FormatOperatorDate,
    ALLOWED_ROLES
} from './../../../utilities/BL/Admin/Operators';

/**
 * Componente principal para la Gestión de Operadores y Sesiones de Administración.
 * [HU-03] y [HU-04]
 * 
 * Permite listar, registrar, editar, cambiar contraseñas, forzar terminación de sesiones activas
 * y eliminar cuentas de operadores del sistema de manera segura.
 */
export const OperatorsManager = () => {
    // --- ESTADOS DE DATOS ---
    const [operators, setOperators] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [alertMessage, setAlertMessage] = useState(null); // { type: 'success' | 'danger' | 'warning' | 'info', text: '' }

    // Usuario actual autenticado
    const currentUserId = useMemo(() => {
        try {
            const rawUser = localStorage.getItem('user');
            if (rawUser) {
                const parsed = JSON.parse(rawUser);
                return parsed?.id || null;
            }
        } catch {
            return null;
        }
        return null;
    }, []);

    const currentUsername = useMemo(() => {
        try {
            const rawUser = localStorage.getItem('user');
            if (rawUser) {
                const parsed = JSON.parse(rawUser);
                return parsed?.username || null;
            }
        } catch {
            return null;
        }
        return null;
    }, []);

    // --- ESTADOS DE MODALES ---
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isChangeMyPasswordModalOpen, setIsChangeMyPasswordModalOpen] = useState(false);
    const [isTerminateModalOpen, setIsTerminateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // Operador seleccionado para acciones
    const [selectedOperator, setSelectedOperator] = useState(null);

    // Estados de Formularios
    const [createForm, setCreateForm] = useState({
        username: '',
        password: '',
        confirmPassword: '',
        role: 'Operador',
        brandId: ''
    });
    const [showCreatePassword, setShowCreatePassword] = useState(false);

    const [editForm, setEditForm] = useState({
        role: 'Operador',
        password: '',
        confirmPassword: '',
        brandId: ''
    });
    const [showEditPassword, setShowEditPassword] = useState(false);

    const [myPasswordForm, setMyPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalError, setModalError] = useState('');

    // --- CARGA DE OPERADORES ---
    const fetchOperators = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await GetOperators();
            if (res && res.success) {
                setOperators(res.data || []);
            } else {
                setAlertMessage({
                    type: 'danger',
                    text: res?.message || 'Error al cargar la lista de operadores.'
                });
            }
        } catch (error) {
            setAlertMessage({
                type: 'danger',
                text: 'Error de conexión al obtener los operadores.'
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOperators();
    }, [fetchOperators]);

    // Limpieza automática de alertas
    useEffect(() => {
        if (alertMessage) {
            const timer = setTimeout(() => {
                setAlertMessage(null);
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [alertMessage]);

    // --- ACCIONES DE OPERADORES ---

    // 1. Abrir Modal de Creación
    const handleOpenCreateModal = () => {
        setCreateForm({
            username: '',
            password: '',
            confirmPassword: '',
            role: 'Operador',
            brandId: ''
        });
        setShowCreatePassword(false);
        setModalError('');
        setIsCreateModalOpen(true);
    };

    // 2. Submit Creación
    const handleSubmitCreate = async (e) => {
        e.preventDefault();
        setModalError('');

        if (createForm.password !== createForm.confirmPassword) {
            setModalError('Las contraseñas no coinciden.');
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await RegisterOperator(createForm);
            if (res && res.success) {
                setAlertMessage({
                    type: 'success',
                    text: `¡Operador "${createForm.username}" registrado exitosamente!`
                });
                setIsCreateModalOpen(false);
                fetchOperators();
            } else {
                setModalError(res?.message || 'Error al registrar el operador.');
            }
        } catch (error) {
            setModalError('Error de conexión con el servidor.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 3. Abrir Modal de Edición
    const handleOpenEditModal = (op) => {
        setSelectedOperator(op);
        setEditForm({
            role: op.role || 'Operador',
            password: '',
            confirmPassword: '',
            brandId: op.brandId || ''
        });
        setShowEditPassword(false);
        setModalError('');
        setIsEditModalOpen(true);
    };

    // 4. Submit Edición
    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        setModalError('');

        if (editForm.password && editForm.password !== editForm.confirmPassword) {
            setModalError('Las nuevas contraseñas no coinciden.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                role: editForm.role,
                brandId: editForm.brandId || null,
                password: editForm.password ? editForm.password : undefined
            };

            const res = await UpdateOperator(selectedOperator.id, payload);
            if (res && res.success) {
                setAlertMessage({
                    type: 'success',
                    text: `¡Operador "${selectedOperator.username}" actualizado correctamente!`
                });
                setIsEditModalOpen(false);
                setSelectedOperator(null);
                fetchOperators();
            } else {
                setModalError(res?.message || 'Error al actualizar el operador.');
            }
        } catch (error) {
            setModalError('Error de conexión con el servidor.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 5. Abrir Modal de Cambio de Contraseña Propia
    const handleOpenMyPasswordModal = () => {
        setMyPasswordForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setModalError('');
        setIsChangeMyPasswordModalOpen(true);
    };

    // 6. Submit Cambio de Contraseña Propia
    const handleSubmitMyPassword = async (e) => {
        e.preventDefault();
        setModalError('');

        if (myPasswordForm.newPassword !== myPasswordForm.confirmPassword) {
            setModalError('La confirmación de la nueva contraseña no coincide.');
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await ChangePassword(myPasswordForm);
            if (res && res.success) {
                setAlertMessage({
                    type: 'success',
                    text: '¡Tu contraseña ha sido actualizada exitosamente!'
                });
                setIsChangeMyPasswordModalOpen(false);
            } else {
                setModalError(res?.message || 'Error al actualizar la contraseña.');
            }
        } catch (error) {
            setModalError('Error de conexión al procesar el cambio de contraseña.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 7. Abrir y Ejecutar Terminación de Sesiones Activas
    const handleOpenTerminateModal = (op) => {
        setSelectedOperator(op);
        setIsTerminateModalOpen(true);
    };

    const handleConfirmTerminate = async () => {
        if (!selectedOperator) return;
        setIsSubmitting(true);
        try {
            const res = await TerminateOperatorSessions(selectedOperator.id);
            if (res && res.success) {
                setAlertMessage({
                    type: 'success',
                    text: res.message || `Sesiones del operador "${selectedOperator.username}" cerradas con éxito.`
                });
                setIsTerminateModalOpen(false);
                setSelectedOperator(null);
                fetchOperators();
            } else {
                setAlertMessage({
                    type: 'danger',
                    text: res?.message || 'No fue posible cerrar las sesiones activas.'
                });
                setIsTerminateModalOpen(false);
            }
        } catch (error) {
            setAlertMessage({
                type: 'danger',
                text: 'Error al enviar la solicitud de cierre de sesión.'
            });
            setIsTerminateModalOpen(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 8. Abrir y Ejecutar Eliminación de Operador
    const handleOpenDeleteModal = (op) => {
        setSelectedOperator(op);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedOperator) return;
        setIsSubmitting(true);
        try {
            const res = await DeleteOperator(selectedOperator.id);
            if (res && res.success) {
                setAlertMessage({
                    type: 'success',
                    text: res.message || `Operador "${selectedOperator.username}" eliminado exitosamente.`
                });
                setIsDeleteModalOpen(false);
                setSelectedOperator(null);
                fetchOperators();
            } else {
                setAlertMessage({
                    type: 'danger',
                    text: res?.message || 'Error al eliminar el operador.'
                });
                setIsDeleteModalOpen(false);
            }
        } catch (error) {
            setAlertMessage({
                type: 'danger',
                text: 'Error de conexión al eliminar el operador.'
            });
            setIsDeleteModalOpen(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- FILTRADO Y MÉTRICAS COMPUTADAS ---
    const filteredOperators = useMemo(() => {
        let list = [...operators];

        if (roleFilter !== 'ALL') {
            list = list.filter(op => (op.role || '').toLowerCase() === roleFilter.toLowerCase());
        }

        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase().trim();
            list = list.filter(op =>
                (op.username && op.username.toLowerCase().includes(query)) ||
                (op.role && op.role.toLowerCase().includes(query)) ||
                (op.brandId && op.brandId.toLowerCase().includes(query))
            );
        }

        return list;
    }, [operators, roleFilter, searchTerm]);

    const metrics = useMemo(() => {
        const total = operators.length;
        const totalActiveSessions = operators.reduce((acc, op) => acc + (op.activeSessionsCount || 0), 0);
        const superAdminsCount = operators.filter(op => (op.role || '').toLowerCase() === 'superadmin' || (op.role || '').toLowerCase() === 'master_admin').length;
        const operatorsCount = operators.filter(op => (op.role || '').toLowerCase() === 'operador' || (op.role || '').toLowerCase() === 'admin').length;

        return {
            total,
            totalActiveSessions,
            superAdminsCount,
            operatorsCount
        };
    }, [operators]);

    return (
        <Base urlToGo="/admin/dashboard">
            <div className="container-fluid pb-5">
                {/* BANNER DE ALERTA GLOBAL */}
                {alertMessage && (
                    <div className={`alert alert-${alertMessage.type} alert-dismissible fade show shadow-sm mb-4`} role="alert">
                        <div className="d-flex align-items-center">
                            <i className={`bi ${alertMessage.type === 'success' ? 'bi-check-circle-fill' : alertMessage.type === 'danger' ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'} fs-4 me-3`}></i>
                            <div>
                                <strong>{alertMessage.type === 'success' ? 'Operación exitosa: ' : 'Atención: '}</strong>
                                {alertMessage.text}
                            </div>
                        </div>
                        <button type="button" className="btn-close" onClick={() => setAlertMessage(null)} aria-label="Cerrar"></button>
                    </div>
                )}

                {/* ENCABEZADO Y ACCIONES PRINCIPALES */}
                <div className="card bg-dark text-white border-secondary shadow-sm mb-4">
                    <div className="card-body p-4">
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                            <div className="d-flex align-items-center gap-3">
                                <div className="rounded-circle bg-danger p-3 d-flex align-items-center justify-content-center shadow" style={{ width: '60px', height: '60px' }}>
                                    <i className="bi bi-people-fill fs-2 text-white"></i>
                                </div>
                                <div>
                                    <h2 className="h4 fw-bold mb-1 text-white">Gestión de Operadores</h2>
                                    <p className="text-secondary mb-0 small">
                                        Administración de cuentas, asignación de roles y control de sesiones activas en tiempo real
                                    </p>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <button
                                    type="button"
                                    className="Btn__Adsl--Red d-inline-flex align-items-center gap-2 px-3 py-2"
                                    onClick={handleOpenCreateModal}
                                >
                                    <i className="bi bi-person-plus-fill"></i>
                                    <span>Nuevo Operador</span>
                                </button>

                                <button
                                    type="button"
                                    className="Btn__Adsl--RedOutline d-inline-flex align-items-center gap-2 px-3 py-2"
                                    onClick={handleOpenMyPasswordModal}
                                    title="Actualizar mi propia clave de acceso"
                                >
                                    <i className="bi bi-key-fill"></i>
                                    <span>Cambiar Mi Contraseña</span>
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 px-3 py-2"
                                    onClick={fetchOperators}
                                    disabled={isLoading}
                                    title="Refrescar lista"
                                >
                                    <i className={`bi bi-arrow-clockwise ${isLoading ? 'spin' : ''}`}></i>
                                </button>
                            </div>
                        </div>

                        {/* TARJETAS DE MÉTRICAS */}
                        <div className="row g-3 mt-3">
                            <div className="col-6 col-md-3">
                                <div className="p-3 rounded bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-center">
                                    <span className="d-block text-secondary small text-uppercase fw-bold">Total Operadores</span>
                                    <span className="fs-3 fw-bold text-white">{metrics.total}</span>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="p-3 rounded bg-success bg-opacity-10 border border-success border-opacity-25 text-center">
                                    <span className="d-block text-success small text-uppercase fw-bold">Sesiones Activas</span>
                                    <span className="fs-3 fw-bold text-success">
                                        <i className="bi bi-broadcast me-1"></i>
                                        {metrics.totalActiveSessions}
                                    </span>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="p-3 rounded bg-danger bg-opacity-10 border border-danger border-opacity-25 text-center">
                                    <span className="d-block text-danger small text-uppercase fw-bold">Super Admins</span>
                                    <span className="fs-3 fw-bold text-white">{metrics.superAdminsCount}</span>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="p-3 rounded bg-primary bg-opacity-10 border border-primary border-opacity-25 text-center">
                                    <span className="d-block text-info small text-uppercase fw-bold">Operadores Gral.</span>
                                    <span className="fs-3 fw-bold text-white">{metrics.operatorsCount}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CONTROLES DE BÚSQUEDA Y FILTRADO */}
                <div className="card bg-dark text-white border-secondary shadow-sm mb-4">
                    <div className="card-body p-3">
                        <div className="row g-3 align-items-center">
                            <div className="col-12 col-md-6">
                                <div className="input-group">
                                    <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control bg-dark text-white border-secondary"
                                        placeholder="Buscar por nombre de usuario o rol..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary border-secondary"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            <i className="bi bi-x"></i>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-6 d-flex flex-wrap justify-content-md-end gap-1">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${roleFilter === 'ALL' ? 'btn-danger' : 'btn-outline-secondary text-white'}`}
                                    onClick={() => setRoleFilter('ALL')}
                                >
                                    Todos ({operators.length})
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${roleFilter === 'SuperAdmin' ? 'btn-danger' : 'btn-outline-secondary text-white'}`}
                                    onClick={() => setRoleFilter('SuperAdmin')}
                                >
                                    SuperAdmin
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${roleFilter === 'BusinessAdmin' ? 'btn-primary' : 'btn-outline-secondary text-white'}`}
                                    onClick={() => setRoleFilter('BusinessAdmin')}
                                >
                                    BusinessAdmin
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${roleFilter === 'Operador' ? 'btn-success' : 'btn-outline-secondary text-white'}`}
                                    onClick={() => setRoleFilter('Operador')}
                                >
                                    Operador
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${roleFilter === 'sound_operator' ? 'btn-info' : 'btn-outline-secondary text-white'}`}
                                    onClick={() => setRoleFilter('sound_operator')}
                                >
                                    DJ / Sonido
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABLA PRINCIPAL DE OPERADORES */}
                <div className="card bg-dark text-white border-secondary shadow-sm">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-dark table-hover table-striped align-middle mb-0">
                                <thead className="table-secondary text-uppercase text-secondary small">
                                    <tr>
                                        <th scope="col" className="ps-4 py-3">Usuario</th>
                                        <th scope="col" className="py-3">Rol Asignado</th>
                                        <th scope="col" className="py-3 text-center">Sesiones Activas</th>
                                        <th scope="col" className="py-3">Fecha Registro</th>
                                        <th scope="col" className="pe-4 py-3 text-end">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5">
                                                <div className="spinner-border text-danger me-2" role="status"></div>
                                                <span className="text-secondary">Cargando operadores del sistema...</span>
                                            </td>
                                        </tr>
                                    ) : filteredOperators.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5">
                                                <i className="bi bi-people display-6 d-block text-secondary mb-2"></i>
                                                <p className="fw-bold mb-1">No se encontraron operadores</p>
                                                <span className="text-secondary small">
                                                    {searchTerm ? 'No hay resultados que coincidan con la búsqueda.' : 'No hay operadores registrados en este momento.'}
                                                </span>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredOperators.map((op) => {
                                            const roleConfig = GetRoleBadgeConfig(op.role);
                                            const isCurrentUser = (currentUserId && op.id && op.id.toLowerCase() === currentUserId.toLowerCase()) ||
                                                                  (currentUsername && op.username && op.username.toLowerCase() === currentUsername.toLowerCase());
                                            const activeCount = op.activeSessionsCount || 0;

                                            return (
                                                <tr key={op.id}>
                                                    {/* USUARIO */}
                                                    <td className="ps-4 py-3">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="rounded-circle bg-secondary bg-opacity-25 d-flex align-items-center justify-content-center text-white fw-bold" style={{ width: '38px', height: '38px' }}>
                                                                {op.username ? op.username.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                            <div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="fw-bold text-white">{op.username}</span>
                                                                    {isCurrentUser && (
                                                                        <span className="badge bg-danger rounded-pill px-2 py-1 small">
                                                                            Tú
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {op.brandId && (
                                                                    <span className="text-secondary small d-block">
                                                                        Marca: {op.brandId}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* ROL */}
                                                    <td className="py-3">
                                                        <span className={`badge ${roleConfig.badgeClass} px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1`}>
                                                            <i className={roleConfig.icon}></i>
                                                            <span>{roleConfig.label}</span>
                                                        </span>
                                                    </td>

                                                    {/* SESIONES ACTIVAS */}
                                                    <td className="py-3 text-center">
                                                        {activeCount > 0 ? (
                                                            <span className="badge bg-success px-3 py-2 rounded-pill shadow-sm d-inline-flex align-items-center gap-1" title={`${activeCount} dispositivo(s) o pestaña(s) autenticadas`}>
                                                                <span className="spinner-grow spinner-grow-sm me-1" style={{ width: '8px', height: '8px' }} role="status"></span>
                                                                {activeCount} {activeCount === 1 ? 'activa' : 'activas'}
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-secondary bg-opacity-50 text-white-50 px-3 py-2 rounded-pill">
                                                                0 activas
                                                            </span>
                                                        )}
                                                    </td>

                                                    {/* FECHA DE REGISTRO */}
                                                    <td className="py-3 text-secondary small">
                                                        <i className="bi bi-calendar3 me-1"></i>
                                                        {FormatOperatorDate(op.createdAt)}
                                                    </td>

                                                    {/* ACCIONES */}
                                                    <td className="pe-4 py-3 text-end">
                                                        <div className="btn-group" role="group">
                                                            {/* EDITAR / RESTABLECER CONTRASEÑA */}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-light"
                                                                onClick={() => handleOpenEditModal(op)}
                                                                title="Editar operador o restablecer contraseña"
                                                            >
                                                                <i className="bi bi-pencil-square me-1"></i>
                                                                <span className="d-none d-lg-inline">Editar</span>
                                                            </button>

                                                            {/* TERMINAR SESIONES FORZOSAMENTE */}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-warning"
                                                                onClick={() => handleOpenTerminateModal(op)}
                                                                disabled={activeCount === 0}
                                                                title={activeCount > 0 ? 'Forzar cierre de todas las sesiones activas' : 'Sin sesiones activas'}
                                                            >
                                                                <i className="bi bi-power me-1"></i>
                                                                <span className="d-none d-lg-inline">Cerrar Sesión</span>
                                                            </button>

                                                            {/* ELIMINAR */}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleOpenDeleteModal(op)}
                                                                disabled={isCurrentUser}
                                                                title={isCurrentUser ? 'No puedes eliminar tu propio usuario' : 'Eliminar operador permanentemente'}
                                                            >
                                                                <i className="bi bi-trash-fill me-1"></i>
                                                                <span className="d-none d-lg-inline">Eliminar</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ========================================================================= */}
                {/* MODAL 1: CREAR NUEVO OPERADOR */}
                {/* ========================================================================= */}
                {isCreateModalOpen && (
                    <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1050 }}>
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content bg-dark text-white border-secondary shadow-lg">
                                <div className="modal-header border-secondary">
                                    <h5 className="modal-title d-flex align-items-center gap-2">
                                        <i className="bi bi-person-plus-fill text-danger"></i>
                                        <span>Crear Nuevo Operador</span>
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setIsCreateModalOpen(false)}
                                        aria-label="Cerrar"
                                        disabled={isSubmitting}
                                    ></button>
                                </div>

                                <form onSubmit={handleSubmitCreate}>
                                    <div className="modal-body p-4">
                                        {modalError && (
                                            <div className="alert alert-danger py-2 px-3 small mb-3">
                                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                {modalError}
                                            </div>
                                        )}

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                Nombre de Usuario <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-person"></i>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control bg-dark text-white border-secondary"
                                                    placeholder="Ej. operador_tribuna"
                                                    value={createForm.username}
                                                    onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                                                    required
                                                    autoFocus
                                                    maxLength={50}
                                                />
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                Rol en el Sistema <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-shield-lock"></i>
                                                </span>
                                                <select
                                                    className="form-select bg-dark text-white border-secondary"
                                                    value={createForm.role}
                                                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                                                    required
                                                >
                                                    <option value="Operador">Operador (Acceso a dinámicas)</option>
                                                    <option value="sound_operator">sound_operator (Cabina DJ / Playlist)</option>
                                                    <option value="BusinessAdmin">BusinessAdmin (Administrador de Negocio)</option>
                                                    <option value="SuperAdmin">SuperAdmin (Acceso total)</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                Contraseña (mínimo 6 caracteres) <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-lock"></i>
                                                </span>
                                                <input
                                                    type={showCreatePassword ? 'text' : 'password'}
                                                    className="form-control bg-dark text-white border-secondary"
                                                    placeholder="••••••••"
                                                    value={createForm.password}
                                                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                                    required
                                                    minLength={6}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary border-secondary"
                                                    onClick={() => setShowCreatePassword(!showCreatePassword)}
                                                    tabIndex="-1"
                                                >
                                                    <i className={`bi ${showCreatePassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                Confirmar Contraseña <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-lock-fill"></i>
                                                </span>
                                                <input
                                                    type={showCreatePassword ? 'text' : 'password'}
                                                    className="form-control bg-dark text-white border-secondary"
                                                    placeholder="••••••••"
                                                    value={createForm.confirmPassword}
                                                    onChange={(e) => setCreateForm({ ...createForm, confirmPassword: e.target.value })}
                                                    required
                                                    minLength={6}
                                                />
                                            </div>
                                        </div>

                                        <div className="mb-1">
                                            <label className="form-label small fw-bold text-secondary">
                                                ID de Marca / Sede (Opcional)
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-dark text-white border-secondary"
                                                placeholder="Ej. santosfc"
                                                value={createForm.brandId}
                                                onChange={(e) => setCreateForm({ ...createForm, brandId: e.target.value })}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-footer border-secondary">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setIsCreateModalOpen(false)}
                                            disabled={isSubmitting}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="Btn__Adsl--Red"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Registrando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check-circle-fill me-2"></i>
                                                    Crear Operador
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========================================================================= */}
                {/* MODAL 2: EDITAR OPERADOR / RESTABLECER CONTRASEÑA */}
                {/* ========================================================================= */}
                {isEditModalOpen && selectedOperator && (
                    <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1050 }}>
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content bg-dark text-white border-secondary shadow-lg">
                                <div className="modal-header border-secondary">
                                    <h5 className="modal-title d-flex align-items-center gap-2">
                                        <i className="bi bi-pencil-square text-warning"></i>
                                        <span>Editar: {selectedOperator.username}</span>
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setIsEditModalOpen(false)}
                                        aria-label="Cerrar"
                                        disabled={isSubmitting}
                                    ></button>
                                </div>

                                <form onSubmit={handleSubmitEdit}>
                                    <div className="modal-body p-4">
                                        {modalError && (
                                            <div className="alert alert-danger py-2 px-3 small mb-3">
                                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                {modalError}
                                            </div>
                                        )}

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                Rol en el Sistema
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-shield-lock"></i>
                                                </span>
                                                <select
                                                    className="form-select bg-dark text-white border-secondary"
                                                    value={editForm.role}
                                                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                                    required
                                                >
                                                    <option value="Operador">Operador (Acceso a dinámicas)</option>
                                                    <option value="sound_operator">sound_operator (Cabina DJ / Playlist)</option>
                                                    <option value="BusinessAdmin">BusinessAdmin (Administrador de Negocio)</option>
                                                    <option value="SuperAdmin">SuperAdmin (Acceso total)</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                ID de Marca / Sede (Opcional)
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-dark text-white border-secondary"
                                                placeholder="Ej. santosfc"
                                                value={editForm.brandId}
                                                onChange={(e) => setEditForm({ ...editForm, brandId: e.target.value })}
                                                maxLength={50}
                                            />
                                        </div>

                                        <hr className="border-secondary my-3" />

                                        <div className="mb-2">
                                            <span className="badge bg-secondary mb-2">Restablecer Contraseña (Opcional)</span>
                                            <p className="text-secondary small mb-2">
                                                Si no deseas cambiar la contraseña de este operador, deja los siguientes campos en blanco.
                                            </p>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                Nueva Contraseña (mínimo 6 caracteres)
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-key"></i>
                                                </span>
                                                <input
                                                    type={showEditPassword ? 'text' : 'password'}
                                                    className="form-control bg-dark text-white border-secondary"
                                                    placeholder="Dejar en blanco para mantener actual"
                                                    value={editForm.password}
                                                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                                                    minLength={editForm.password ? 6 : undefined}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary border-secondary"
                                                    onClick={() => setShowEditPassword(!showEditPassword)}
                                                    tabIndex="-1"
                                                >
                                                    <i className={`bi ${showEditPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                </button>
                                            </div>
                                        </div>

                                        {editForm.password && (
                                            <div className="mb-3">
                                                <label className="form-label small fw-bold text-secondary">
                                                    Confirmar Nueva Contraseña
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                        <i className="bi bi-lock-fill"></i>
                                                    </span>
                                                    <input
                                                        type={showEditPassword ? 'text' : 'password'}
                                                        className="form-control bg-dark text-white border-secondary"
                                                        placeholder="Repite la nueva contraseña"
                                                        value={editForm.confirmPassword}
                                                        onChange={(e) => setEditForm({ ...editForm, confirmPassword: e.target.value })}
                                                        required={Boolean(editForm.password)}
                                                        minLength={6}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="modal-footer border-secondary">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setIsEditModalOpen(false)}
                                            disabled={isSubmitting}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="Btn__Adsl--Red"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Guardando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check2-circle me-2"></i>
                                                    Guardar Cambios
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========================================================================= */}
                {/* MODAL 3: CAMBIAR MI PROPIA CONTRASEÑA */}
                {/* ========================================================================= */}
                {isChangeMyPasswordModalOpen && (
                    <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1050 }}>
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content bg-dark text-white border-secondary shadow-lg">
                                <div className="modal-header border-secondary">
                                    <h5 className="modal-title d-flex align-items-center gap-2">
                                        <i className="bi bi-shield-lock-fill text-danger"></i>
                                        <span>Cambiar Mi Contraseña</span>
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setIsChangeMyPasswordModalOpen(false)}
                                        aria-label="Cerrar"
                                        disabled={isSubmitting}
                                    ></button>
                                </div>

                                <form onSubmit={handleSubmitMyPassword}>
                                    <div className="modal-body p-4">
                                        {modalError && (
                                            <div className="alert alert-danger py-2 px-3 small mb-3">
                                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                {modalError}
                                            </div>
                                        )}

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                Contraseña Actual <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-key"></i>
                                                </span>
                                                <input
                                                    type={showCurrentPassword ? 'text' : 'password'}
                                                    className="form-control bg-dark text-white border-secondary"
                                                    placeholder="Escribe tu contraseña actual"
                                                    value={myPasswordForm.currentPassword}
                                                    onChange={(e) => setMyPasswordForm({ ...myPasswordForm, currentPassword: e.target.value })}
                                                    required
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary border-secondary"
                                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                    tabIndex="-1"
                                                >
                                                    <i className={`bi ${showCurrentPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label small fw-bold text-secondary">
                                                Nueva Contraseña (mínimo 6 caracteres) <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-lock"></i>
                                                </span>
                                                <input
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    className="form-control bg-dark text-white border-secondary"
                                                    placeholder="Escribe tu nueva contraseña"
                                                    value={myPasswordForm.newPassword}
                                                    onChange={(e) => setMyPasswordForm({ ...myPasswordForm, newPassword: e.target.value })}
                                                    required
                                                    minLength={6}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary border-secondary"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    tabIndex="-1"
                                                >
                                                    <i className={`bi ${showNewPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mb-1">
                                            <label className="form-label small fw-bold text-secondary">
                                                Confirmar Nueva Contraseña <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-secondary bg-opacity-25 border-secondary text-secondary">
                                                    <i className="bi bi-lock-fill"></i>
                                                </span>
                                                <input
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    className="form-control bg-dark text-white border-secondary"
                                                    placeholder="Repite tu nueva contraseña"
                                                    value={myPasswordForm.confirmPassword}
                                                    onChange={(e) => setMyPasswordForm({ ...myPasswordForm, confirmPassword: e.target.value })}
                                                    required
                                                    minLength={6}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="modal-footer border-secondary">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setIsChangeMyPasswordModalOpen(false)}
                                            disabled={isSubmitting}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="Btn__Adsl--Red"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Actualizando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check-circle-fill me-2"></i>
                                                    Actualizar Contraseña
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========================================================================= */}
                {/* MODAL 4: CONFIRMACIÓN TERMINAR SESIONES ACTIVAS */}
                {/* ========================================================================= */}
                {isTerminateModalOpen && selectedOperator && (
                    <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1050 }}>
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content bg-dark text-white border-warning shadow-lg">
                                <div className="modal-header border-secondary">
                                    <h5 className="modal-title d-flex align-items-center gap-2 text-warning">
                                        <i className="bi bi-exclamation-triangle-fill"></i>
                                        <span>Forzar Cierre de Sesiones</span>
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setIsTerminateModalOpen(false)}
                                        aria-label="Cerrar"
                                        disabled={isSubmitting}
                                    ></button>
                                </div>

                                <div className="modal-body p-4 text-center">
                                    <div className="rounded-circle bg-warning bg-opacity-10 text-warning p-3 d-inline-flex mb-3">
                                        <i className="bi bi-power display-5"></i>
                                    </div>
                                    <p className="fs-5 mb-2">
                                        ¿Deseas cerrar todas las sesiones activas de <strong>"{selectedOperator.username}"</strong>?
                                    </p>
                                    <p className="text-secondary small mb-0">
                                        Se revocarán inmediatamente todos los tokens de refresco del operador ({selectedOperator.activeSessionsCount || 0} sesión(es) activa(s)). El usuario será desconectado en su próxima petición.
                                    </p>
                                </div>

                                <div className="modal-footer border-secondary justify-content-center">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-4"
                                        onClick={() => setIsTerminateModalOpen(false)}
                                        disabled={isSubmitting}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-warning px-4 fw-bold"
                                        onClick={handleConfirmTerminate}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Cerrando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-power me-2"></i>
                                                Sí, Cerrar Sesiones
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========================================================================= */}
                {/* MODAL 5: CONFIRMACIÓN ELIMINAR OPERADOR */}
                {/* ========================================================================= */}
                {isDeleteModalOpen && selectedOperator && (
                    <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1050 }}>
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content bg-dark text-white border-danger shadow-lg">
                                <div className="modal-header border-secondary">
                                    <h5 className="modal-title d-flex align-items-center gap-2 text-danger">
                                        <i className="bi bi-trash-fill"></i>
                                        <span>Confirmar Eliminación</span>
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setIsDeleteModalOpen(false)}
                                        aria-label="Cerrar"
                                        disabled={isSubmitting}
                                    ></button>
                                </div>

                                <div className="modal-body p-4 text-center">
                                    <div className="rounded-circle bg-danger bg-opacity-10 text-danger p-3 d-inline-flex mb-3">
                                        <i className="bi bi-person-x-fill display-5"></i>
                                    </div>
                                    <p className="fs-5 mb-2">
                                        ¿Estás seguro de eliminar permanentemente al operador <strong>"{selectedOperator.username}"</strong>?
                                    </p>
                                    <p className="text-danger small mb-0">
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        Esta acción no se puede deshacer. Se eliminarán todos sus registros y accesos asociados.
                                    </p>
                                </div>

                                <div className="modal-footer border-secondary justify-content-center">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-4"
                                        onClick={() => setIsDeleteModalOpen(false)}
                                        disabled={isSubmitting}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger px-4 fw-bold"
                                        onClick={handleConfirmDelete}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Eliminando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-trash-fill me-2"></i>
                                                Sí, Eliminar Operador
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Base>
    );
};

export default OperatorsManager;
